import React from 'react';

const generateListItems = (listItems: (string | React.ReactNode)[], type: 'bullet' | 'alphabetical' = 'bullet') => {
  const listStyleType = type === 'alphabetical' ? 'lower-alpha' : 'disc';
  const ListElement = type === 'alphabetical' ? 'ol' : 'ul';

  return (
    <ListElement style={{ paddingInlineStart: '24px', margin: '8px 0', listStyleType }}>
      {listItems.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i}>{item}</li>
      ))}
    </ListElement>
  );
};

interface SectionItem {
  number: string; // 1.1, 8.4 etc
  title: string;
  listItems?: (string | React.ReactNode)[];
  renderContent: (listItems?: (string | React.ReactNode)[]) => JSX.Element;
}

/* eslint-disable max-len */
export const sectionsDE: SectionItem[] = [
  {
    number: 'Introduction',
    title: '',
    renderContent: () => (
      <div>
        Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die vertraglichen Beziehungen zwischen der Sephyre GmbH
        (nachfolgend „Sephyre“) und ihren Kunden (nachfolgend „Kunde/n“) für die nachstehenden Dienstleis- tungen,
        welche Sephyre für ihre Kunden erbringt:
        {generateListItems([
          'Speditionsdienstleistungen: Sephyre agiert als Spediteurin, die Transportdienstleistungen und damit zusammenhängende Dienstleistungen vermittelt (vgl. Ziff. 1.1).',
          'Frachtführerdienstleistungen: In abschliessend definierten Fällen handelt Sephyre als Frachtführerin (vgl. Ziff. 1.2).',
          'Dienstleistungen im Gefahrgutbereich: Sephyre erbringt verschiedene Dienstleistungen im Gefahrengutbereich (vgl. Ziff. 1.3).',
          'Weitere Dienstleistungen: Sephyre erbringt zusätzliche Dienstleistungen, die direkt oder indirekt im Zusammenhang mit einem Transport stehen oder unabhängig davon in Auftrag gegeben werden, wie z.B. Verpackungsdienstleistungen und Lagerhaltung (vgl. Ziff. 1.4).',
        ], 'bullet')}
        <p>
          Sephyre erbringt sämtliche Dienstleistungen zu den nachstehend aufgeführten Bedingungen. Andere Bedingungen,
          ob mündlich oder schriftlich mitgeteilt, haben keine Wirkung, es sei denn, Sephyre hat im Einzelfall
          ausdrücklich schriftlich zugestimmt. Ergänzende oder abweichende Bedingungen, die in einem vom Kunden zu
          irgendeinem Zeitpunkt an Sephyre übermittelten Dokument oder E-Mail enthalten sind, einschliesslich, aber
          nicht abschliessend, allgemeine Geschäftsbedingungen des Kunden, gelten als von Sephyre zurückgewiesen und
          haben keine Gültigkeit, auch wenn solche Bedingungen von Sephyre nicht ausdrücklich zurückgewiesen wurden.
        </p>
        <p>
          Mit Erteilung des Auftrags an Sephyre akzeptiert der
          Kunde die vorliegenden AGB von Sephyre.
        </p>
        <p>
          Diese AGB bilden zusammen mit der Auftragsbestätigung die gesamte Vereinbarung zwischen Sephyre und dem
          Kunden, es sei denn, die Parteien hätten schriftlich etwas anderes vereinbart. Im Falle eines Konflikts
          zwischen der Auftragsbestätigung von Sephyre und den AGB geniessen abweichende Vereinbarungen in der
          Auftragsbestätigung von Sephyre Vorrang.
        </p>
      </div>
    ),
  },
  {
    number: '1',
    title: 'Dienstleistungen',
    renderContent: () => (<div />),
  },
  {
    number: '1.1',
    title: 'Speditionsdienstleistungen',
    listItems: [
      'Den Abschluss von Verträgen mit Frachtführern, anderen Spediteuren, Lagerhaltern und sonstigen Dritten (z.B. Verzollungsagenten), für die Durchführung des Transports und sämtlicher damit verbundenen Dienstleistungen.',
      'Die Auswahl geeigneter Transportunternehmen und Lagerhalter sowie die Abstimmung der Transportlogistik.',
    ],
    renderContent: (listItems) => (
      <>
        Im Rahmen der Erbringung von Speditionsdienstleistungen agiert Sephyre als Vermittlerin. Dabei übernimmt Sephyre
        gegen Vergütung die Organisation der Versendung oder Weitersendung von Gütern auf Rechnung des Kunden, jedoch in
        eigenem Namen.
        <br />
        Dies umfasst insbesondere, aber nicht abschliessend:
        {generateListItems(listItems || [])}
        Die Tätigkeit von Sephyre beschränkt sich auf die Vermittlung und Organisation der Transport- und
        Logistikdienstleistungen. Dies gilt auch dann, wenn Sephyre mit dem Kunden einen Fixpreis für den Transport und
        die weiteren Dienstleistungen vereinbart und damit als Fixkostenspediteur agiert. In einem solchen Fall erbringt
        Sephyre die vereinbarten Dienstleistungen ebenfalls in eigenem Namen, bleibt jedoch Vermittlerin zwischen dem
        Kunden und den von Sephyre beauftragten Subunterneh- mern (z. B. Frachtführer, Lagerhalter). Sephyre führt
        selbst keine Transporte mit eigenen Mitteln durch, es sei denn, dies wird gesondert als
        Frachtführerdienstleistung vereinbart (vgl. Ziff. 1.2).
      </>
    ),
  },
  {
    number: '1.2',
    title: 'Frachtführerdienstleistungen',
    listItems: [
      'Sephyre führt Transporte mit eigenen Transportmitteln aus (Selbsteintritt).',
      'Sephyre stellt eigene Transportdokumente mit Auslieferungsverpflichtung aus',
      'Bei rein europäischen Landtransporten (vorbehältlich reiner Eisenbahntransport), sofern sich Sephyre nicht ausdrücklich als Vermittlerin bezeichnet und auch als solche handelt.',
    ],
    renderContent: (listItems) => (
      <>
        In nachstehenden, abschliessen definierten, Fällen kommt Sephyre die Stellung einer Frachtführerin zu:
        {generateListItems(listItems || [])}
      </>
    ),
  },
  {
    number: '1.3',
    title: 'Dienstleistungserbringung im Gefahrengutbereich',
    listItems: [
      'Externe Gefahrgutbeauftragte: Überwachung und Beratung des Kunden zur Einhaltung gesetzlicher Vorschriften.',
      'Gefahrgutverpackung: Fachgerechte Ver-packung und Kennzeichnung von Gefahrgut gemäss geltenden Vorschriften.',
      'Beratung: Unterstützung bei der Erstellung von Sicherheitsdatenblättern und Schulung von Mitarbeitern.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre erbringt für den Kunden bei separater Beauftragung umfassende Dienstleistungen im Bereich Gefahrgut, einschliesslich:
        {generateListItems(listItems || [])}
      </>
    ),
  },
  {
    number: '1.4',
    title: 'Weitere Dienstleistungen',
    renderContent: () => (
      <>
        Sephyre erbringt für den Kunden bei separater Beauftragung weitere Dienstleistungen, die direkt oder indirekt im Zusammenhang mit einem Transport stehen oder unabhängig davon in Auftrag gegeben werden. Insbesondere, aber nicht abschliessend, umfasst dies Verpackungsdienstleistungen und Lagerhaltung.
      </>
    ),
  },
  {
    number: '2',
    title: 'Angebot, Vertragsschluss und Entgelt',
    renderContent: () => (
      <>
        Die Bestimmungen unter Ziff. 2.1-2.5 gelten für sämtliche Verträge zwischen Sephyre und dem Kunden, sofern unter den Besonderen Bestimmungen gemäss Ziff. 4-7 nicht davon abgewichen wird.
      </>
    ),
  },
  {
    number: '2.1',
    title: 'Unverbindliches Angebot (Offerte)',
    renderContent: () => (
      <>
        Alle Angebote von Sephyre sind unverbindlich.
      </>
    ),
  },
  {
    number: '2.2',
    title: 'Auftragserteilung',
    renderContent: () => (
      <>
        Der Kunde erteilt den Auftrag an Sephyre grundsätzlich online über die Homepage von Sephyre. Alternativ kann der Auftrag auch schriftlich (inkl. elektronische Mitteilung per E-Mail, SMS etc.) oder mündlich (per Telefon) erteilt werden. Nach Eingang des Auftrags bei Sephyre erhält der Kunde eine Eingangsbestätigung, die lediglich den Erhalt des Auftrags dokumentiert. Diese Eingangsbestätigung stellt keine Auftragsbestätigung im Sinne von 2.3 dar und begründet keinen Anspruch auf die Annahme oder Ausführung des Auftrags durch Sephyre.
        <br />
        <br />
        Bei mündlicher oder telefonischer Auftragserteilung trägt der Kunde bis zum Eintreffen der Auftragsbestätigung nach Ziff. 2.3 bei Sephyre das Risiko für Übermittlungsfehler. Sephyre übernimmt keine Haftung für Schäden oder Verzögerungen, die aus fehlerhafter oder unvollständiger Übermittlung resultieren.
      </>
    ),
  },
  {
    number: '2.3',
    title: 'Zustandekommen des Vertrags',
    listItems: [
      'mit Versand bzw. Abgabe der Auftragsbestätigung durch Sephyre oder',
      'mit der Übergabe der Ware an den Transporteur, wenn diese vor der Auftragsbestätigung erfolgt.',
    ],
    renderContent: (listItems) => (
      <>
        Der Vertrag kommt erst zustande:
        {generateListItems(listItems || [], 'alphabetical')}
        Sephyre behält sich das Recht vor, Aufträge jederzeit und ohne Angabe von Gründen abzulehnen. Ein Anspruch auf Annahme oder Durchführung eines Auftrags besteht nicht.
        <br />
        <br />
        Bereits angenommene Aufträge können aus wichtigen Gründen oder aus Gründen, die Sephyre nicht zu vertreten hat, jederzeit storniert werden. Solche Gründe umfassen insbesondere, aber nicht abschliessend, höhere Gewalt i.S.v. Ziff. 8.9, Streiks oder Arbeitsniederlegungen, unvorhersehbare technische Defekte an Transportmitteln, Insolvenz oder Betriebsaufgabe eines beauftragten Transporteurs oder Subunternehmers, Stornierung oder Nichterfüllung des Auftrags durch den Transporteur oder Subunternehmer, Sicherheitsrisiken oder behördliche Auflagen, die eine Durchführung des Transports verhindern oder übermässig erschweren.
        <br />
        <br />
        In einem solchen Fall wird Sephyre den Kunden unverzüglich informieren. Weitergehende Ansprüche des Kunden, insbesondere Schadenersatzansprüche, sind ausgeschlossen.
      </>
    ),
  },
  {
    number: '2.4',
    title: 'Vergütung von Sephyre',
    renderContent: () => (
      <>
        Der Kunde schuldet Sephyre AG die Vergütung gemäss der Offerte von Sephyre, sofern die Parteien nicht ausdrücklich eine anderweitige Vereinbarung getroffen haben. Falls Sephyre keine Offerte abgegeben hat bzw. die Parteien keine anderweitige Vereinbarung getroffen haben, berechnet sich die vom Kunden geschuldete Vergütung nach den Besonderen Bestimmungen für die jeweiligen Dienstleistungen (vgl. Ziff. 4.2, 5.2, 6.2 und 7.2)
        <br />
        <br />
        Alle Preise verstehen sich mangels anderer schriftlicher Vereinbarungen netto (ohne MWST). Die MWST ist vom Kunden zusätzlich geschuldet.
        <br />
        <br />
        Preisänderungen aufgrund von Treibstoffzuschlägen, Wechselkursschwankungen oder behördlichen Abgaben bleiben vorbehalten. Sephyre ist berechtigt, solche Preiserhöhungen an den Kunden weiterzugeben.
        <br />
        <br />
        Falls nach Auftragserteilung Änderungen eintreten, die eine Anpassung des Preises erfordern (z. B. Zusatzleistungen, Verzögerungen etc.), behält sich Sephyre das Recht vor, die Preisberechnung entsprechend zu anzupassen. Sephyre ist berechtigt, Preiserhöhungen an den Kunden weiterzugeben.
      </>
    ),
  },
  {
    number: '2.5',
    title: 'Ersatz für Vorschüsse und Auslagen',
    renderContent: () => (
      <>
        Der Kunde hat Sephyre alle im Interesse des Kunden getätigten Vorschüsse, Auslagen und Verwendungen zu ersetzen, zuzüglich Zins von 5% ab Fälligkeit. Sephyre haftet nicht für die unrichtige Erhebung von Frachten, Zöllen, Abgaben oder Gebühren, und der Kunde stellt Sephyre diesbezüglich von allen Ansprüchen frei.
      </>
    ),
  },
  {
    number: '3',
    title: 'Auftragsabwicklung',
    renderContent: () => (
      <>
        Die Bestimmungen unter Ziff. 3.1-3.11 gelten für sämtliche Verträge zwischen Sephyre und dem Kunden, sofern unter den Besonderen Bestimmungen gemäss Ziff. 4-7 nicht davon abgewichen wird.
      </>
    ),
  },
  {
    number: '3.1',
    title: 'Prüfung des Auftrags',
    renderContent: () => (
      <>
        Sephyre prüft den Auftrag auf Plausibilität, ist jedoch nicht verpflichtet, die Sendung, Transportgefässe, Gewichte, Masse oder andere Angaben (z. B. Gefahrgutklassen) zu kontrollieren, es sei denn, dies wurde ausdrücklich vereinbart. Bei Unklarheiten informiert Sephyre den Kunden umgehend.
      </>
    ),
  },
  {
    number: '3.2',
    title: 'Informationspflicht des Kunden',
    listItems: [
      'Gefahrgüter (einschliesslich UN-Nummer, Klassifizierung, Verpackungsgruppe und erforderlicher Sicherheitsmassnahmen);',
      'Verzollung (einschliesslich Zolltarifnummer); sowie',
      'gegebenenfalls erforderliche Export- und Importbewilligungen (bspw. für Dual-Use-Güter, sanktionierte Waren, genehmigungspflichtige Technologien oder sonstige Güter, die Aus- oder Einfuhr- oder Transitbeschränkungen unterliegen).',
    ],
    renderContent: (listItems) => (
      <>
        Der Kunde ist verpflichtet, Sephyre bei der Auftragserteilung alle für die ordnungsgemässe Abwicklung des Auftrags erforderlichen Informationen vollständig und korrekt mitzuteilen und die erforderlichen Dokumente zu übergeben. Dies gilt insbesondere, aber nicht abschliessend, für
        {generateListItems(listItems || [])}
        Ohne ausdrückliche Information und Vorlage entsprechender Dokumente durch den Kunden darf Sephyre davon ausgehen, dass keine besonderen Vorgaben und keine Bewilligungspflichten bestehen. Der Kunde haftet für unrichtige, unvollständige oder unterlassene Angaben und stellt Sephyre von sämtlichen daraus resultierenden Schäden, Strafen oder sonstigen nachteiligen Folgen frei. Dies gilt insbesondere auch für Forderungen von Transporteuren, Subunternehmern, anderen an der Transportkette beteiligten Dritten sowie für behördliche Anordnungen, Auflagen, Bussgelder oder sonstige Forderungen von Behörden, die gegenüber Sephyre aufgrund fehlerhafter oder unvollständiger Informationen des Kunden geltend gemacht werden.
      </>
    ),
  },
  {
    number: '3.3',
    title: 'Änderung des Auftrags',
    renderContent: () => (
      <>
        Sephyre AG ist berechtigt, Änderungen am Auftrag des Kunden vorzunehmen, soweit dies zur ordnungsgemässen Durchführung des Auftrags erforderlich ist. Dies umfasst insbesondere die Berichtigung oder Ergänzung fehlerhafter oder unvollständiger Angaben im Auftragsformular, wie beispielsweise falsche oder unvollständige Zolltarifnummern, ungenaue Empfänger- oder Absenderadressen, unzutreffende Gewichts- oder Volumenangaben sowie fehlerhafte Klassifizierungen von Gefahrgut. Ebenso können notwendige Informationen ergänzt werden, um gesetzliche oder behördliche Vorschriften einzuhalten.
        <br />
        <br />
        Falls erforderlich, kann Sephyre zudem die Auftragsmodalitäten anpassen. Dies betrifft unter anderem die Änderung des gewählten Transportmittels, beispielsweise den Wechsel von Strassen auf Schienentransport oder von Luft- auf Seefracht. Auch die Transportroute kann modifiziert werden, sofern dies notwendig ist. Zudem kann Sephyre die Lagerungs- oder Umschlagbedingungen anpassen, insbesondere bei speziellen Erfordernissen wie Temperaturführung oder Sicherheitsvorkehrungen. Sollte eine Anpassung der Lieferzeiten aufgrund unvermeidbarer Verzögerungen erforderlich sein, ist Sephyre berechtigt, diese entsprechend zu modifizieren.
        <br />
        <br />
        Sämtliche von Sephyre vorgenommenen Änderungen sind für den Kunden im Buchungssystem von Sephyre oder aus der E-Mailmitteilung von Sephyre ersichtlich. Der Kunde ist verpflichtet, von Sephyre vorgenommene Änderungen unverzüglich zu prüfen. Beanstandungen müssen umgehend schriftlich gegenüber Sephyre angezeigt werden, ansonsten die Änderungen als akzeptiert gelten. Allfällige Mehrkosten, die durch die Änderungen entstehen, gehen vollumfänglich zulasten des Kunden. Sephyre haftet nicht für Verzögerungen oder zusätzliche Kosten, die durch unvollständige oder fehlerhafte Angaben des Kunden verursacht wurden.
      </>
    ),
  },
  {
    number: '3.4',
    title: 'Prüfungspflicht des Kunden für übermittelte Dokumente',
    renderContent: () => (
      <>
        Der Kunde ist verpflichtet, ihm von Sephyre übermittelte Dokumente, insbesondere Auftragsbestätigung, Transportdokumente, Luftfrachtbriefe (AWB), Frachtbriefe, Gefahrengutbegleitpapiere, Zollpapiere, Rechnungen und Liefernachweise, unverzüglich nach Erhalt auf Richtigkeit und Vollständigkeit zu prüfen.
        <br />
        <br />
        Beanstandungen müssen umgehend schriftlich gegenüber Sephyre gemeldet werden. Erfolgt keine rechtzeitige Beanstandung, gelten die Dokumente als vom Kunden akzeptiert.
      </>
    ),
  },
  {
    number: '3.5',
    title: 'Verantwortlichkeit für Ladehilfsmittel und Verpackung',
    renderContent: () => (
      <>
        Der Kunde ist dafür verantwortlich, dass Ladehilfsmittel transportfähig sind. Die ordnungsgemässe Verpackung der Ware ist Sache des Kunden, es sei denn, Sephyre wird ausdrücklich mit der Verpackung beauftragt.
      </>
    ),
  },
  {
    number: '3.6',
    title: 'Lieferfristen',
    renderContent: () => (
      <>
        Lieferfristen sind nur verbindlich, wenn sie schriftlich vereinbart werden. Andernfalls übernimmt Sephyre keine Gewähr für die Einhaltung von Lieferterminen.
      </>
    ),
  },
  {
    number: '3.7',
    title: 'Hochwertige oder speziell zu behandelnde Güter',
    renderContent: () => (
      <>
        Der Kunde muss hochwertige Güter oder Güter, die besondere Behandlungen (z. B. Kühlung, Gefahrgutvorschriften) erfordern, im Auftrag ausdrücklich bezeichnen und die erforderlichen Massnahmen mitteilen.
      </>
    ),
  },
  {
    number: '3.8',
    title: 'Transportversicherung',
    renderContent: () => (
      <>
        Sephyre schliesst eine Transportversicherung nur auf ausdrückliches Verlangen des Kunden ab.
        <br />
        <br />
        Wünscht der Kunde eine Versicherung, ohne spezifische Instruktionen zu erteilen, wird eine möglichst umfassende Transportversicherung abgeschlossen, wobei mittelbare oder indirekte Schäden (z. B. entgangener Gewinn) nicht gedeckt sind. Der Kunde ist für eine ergänzende Versicherung verantwortlich.
      </>
    ),
  },
  {
    number: '3.9',
    title: 'Be- und Entladung',
    renderContent: () => (
      <>
        Die Be- und Entladung der Güter obliegt dem Absender bzw. Empfänger. Unterstützt der Chauffeur bei diesen Tätigkeiten, gilt er als Hilfsperson des Absenders oder Empfängers.
      </>
    ),
  },
  {
    number: '3.10',
    title: 'Zwischenlagerung',
    renderContent: () => (
      <>
        Verweigert der Empfänger die Annahme oder wird die Sendung unterwegs aus Gründen, die Sephyre nicht zu vertreten hat, aufgehalten, hat Sephyre das Recht, die Ware auf Kosten und Gefahr des Kunden einzulagern.
      </>
    ),
  },
  {
    number: '3.11',
    title: 'Warenwertnachnahme (COD)',
    renderContent: () => (
      <>
        Die Warenwertnachnahme (COD; Cash on Delivery) wird nur auf schriftliche Instruktion des Kunden und gegen zusätzliche Gebühr (Nachnahmeprovision) durchgeführt. Die Auslieferung erfolgt nur gegen Barzahlung oder gegen ein unwiderrufliches Zahlungsversprechen einer erstklassigen Bank.
      </>
    ),
  },
  {
    number: '4',
    title: 'Besondere Bestimmungen für Speditionsdienstleistungen',
    renderContent: () => (<div />),
  },
  {
    number: '4.1',
    title: 'Leistungsumfang',
    renderContent: () => (
      <>
        Sephyre agiert als Spediteurin i.S.v. Ziff. 1.1 hiervor und vermittelt Transportdienstleistungen. Sephyre organisiert die Versendung oder Weiterleitung von Gütern und schliesst Verträge mit Dritten (z. B. Frachtführer, Lagerhalter) in eigenem Namen, aber auf Rechnung des Kunden ab.
      </>
    ),
  },
  {
    number: '4.2',
    title: 'Preisberechnung',
    listItems: [
      <>
        <b>Kosten und Gebühren der beauftragten Unternehmer</b>
        <br />
        Hierzu gehören insbesondere die Entgelte für Frachtführer, Unterfrachtführer, Verzollungsagenturen, Lagerhalter sowie sonstige an der Transportkette beteiligte Dienstleister.
      </>,
      <>
        <b>Kommissionsgebühr von Sephyre</b>
        <br />
        Zusätzlich erhebt Sephyre eine Kommissionsgebühr für die Koordination und Abwicklung der Speditionsdienstleistung. Diese beträgt in der Regel 10% des Gesamtauftragswerts, sofern nichts anderes vereinbart wurde.
        <br />
        <br />
        Sephyre behält sich das Recht vor, in besonderen Fällen eine abweichende Kommissionsgebühr zu berechnen, insbesondere bei aussergewöhnlichem Aufwand oder speziellen Kundenanforderungen.
      </>,
      <>
        <b>Zusätzliche Kosten</b>
        <br />
        Falls im Rahmen der Transportdurchführung weitere Kosten anfallen, werden diese dem Kunden separat in Rechnung gestellt. Der Kunde verpflichtet sich, alle zusätzlichen Kosten zu übernehmen, sofern sie zur ordnungsgemässen Durchführung des Auftrags erfor- derlich sind. Im Übrigen gelten die Bestimmungen in Ziff. 2.4 und 2.5.
      </>,
    ],
    renderContent: (listItems) => (
      <>
        Falls Sephyre keine Offerte abgegeben hat bzw. die Parteien keine anderweitige Vereinbarung getroffen haben, setzt sich der Preis für die Speditionsdienstleistungen aus folgenden Bestandteilen. zusammen:
        {generateListItems(listItems || [], 'alphabetical')}
      </>
    ),
  },
  {
    number: '4.3',
    title: 'Haftung',
    listItems: [
      'Verlust oder Beschädigung der Güter: 1.15 Sonderziehungsrechte pro Kilogramm Bruttogewicht der betroffenen Sendung.',
      'Verspätungsschäden: Hälfte des Netto-Frachtbetrags.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre haftet für die sorgfältige Ausführung des Auftrags. Die Haftung für Unterbeauftragte beschränkt sich auf deren sorgfältige Auswahl und Instruktion.
        <br />
        <br />
        Vorbehältlich abweichender zwingender gesetzlicher Vorschriften, geltend für die Haftung von Sephyre folgende Haftungsobergrenzen:
        {generateListItems(listItems || [])}
        Die Haftung von Sephyre pro Schadensereignis ist auf maximal 10&apos;000 Sonderziehungsrechte (SZR) begrenzt. Eine darüber hinausgehende Haftung ist, soweit gesetzlich zulässig, ausgeschlossen. Sephyre haftet nicht für Schäden oder Verzögerungen, die durch höhere Gewalt verursacht werden.
      </>
    ),
  },
  {
    number: '5',
    title: 'Besondere Bestimmungen für Frachtführerdienstleistungen',
    renderContent: () => (<div />),
  },
  {
    number: '5.1',
    title: 'Leistungsumfang',
    renderContent: () => (
      <>
        In den abschliessend aufgezählten Fällen gemäss Ziff. 1.2 handelt Sephyre als Frachtführerin und führt Transporte zu den mit dem Kunden vereinbarten Bedingungen aus.
      </>
    ),
  },
  {
    number: '5.2',
    title: 'Preisberechnung',
    listItems: [
      <>
        <b>Grundlage der Preisberechnung</b>
        <br />
        Die Preisberechnung erfolgt auf Basis der tatsächlich erbrachten Leistungen und der zurückgelegten Strecke, des Gewichts, Volumens, des gewählten Transportmittels oder anderer transportrelevanter Faktoren. Zuschläge für besondere Leistungen wie Expressversand, temperaturgeführte Transporte oder Sondertransporte werden gesondert berechnet.
      </>,
      <>
        <b>Zusätzliche Kosten</b>
        <br />
        Alle zusätzlichen Kosten, die im Zusammenhang mit der Durchführung des Transports anfallen (z. B. Mautgebühren, Verzollungskosten, Wartezeiten, Standgelder, Umschlagkosten), werden dem Kunden gesondert in Rechnung gestellt. Der Kunde verpflichtet sich, alle zusätzlichen Kosten zu übernehmen. Im Übrigen gelten die Bestimmungen in Ziff. 2.4 und 2.5.
      </>,
    ],
    renderContent: (listItems) => (
      <>
        Sofern Sephyre keine Offerte abgegeben hat bzw. die Parteien keine anderweitige Vereinbarung getroffen haben, bestimmt sich der Preis für die Frachtführerdienstleistungen von Sephyre nach den folgenden Grundsätzen:
        {generateListItems(listItems || [], 'alphabetical')}
      </>
    ),
  },
  {
    number: '5.3',
    title: 'Haftung',
    listItems: [
      'Verlust oder Beschädigung der Güter bei rein schweizerisch inländischen Strassentransporten: 1.15 Sonderziehungsrechte pro Kilogramm Bruttogewicht der betroffenen Sendung.',
      'Verlust oder Beschädigung der Güter bei grenzüberschreitenden europäischen Strassentransporten: 8.33 Sonderziehungsrechte pro Kilogramm Bruttogewicht der betroffenen Sendung.',
      'Verspätungsschäden: Hälfte des Netto-Frachtbetrags.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre trägt die Frachtführerhaftung für die ganze Transportstrecke. Vorbehalten bleibt ein sich nur auf eine Teilstrecke beziehender Selbsteintritt von Sephyre.
        <br />
        <br />
        Vorbehältlich abweichender zwingender gesetzlicher Vorschriften, geltend für die Haftung von Sephyre folgende Haftungsobergrenzen:
        {generateListItems(listItems || [])}
        Die Haftung von Sephyre pro Schadensereignis ist auf maximal 10&apos;000 Sonderziehungsrechte (SZR) begrenzt. Eine darüber hinausgehende Haftung ist, soweit gesetzlich zulässig, ausgeschlossen. Sephyre haftet nicht für Schäden oder Verzögerungen, die durch höhere Gewalt verursacht werden.
      </>
    ),
  },
  {
    number: '6',
    title: 'Besondere Bestimmung für die Dienstleistungserbringung im Gefahrgutbereich',
    renderContent: () => (<div />),
  },
  {
    number: '6.1',
    title: 'Leistungsumfang',
    renderContent: () => (
      <>
        Sephyre erbringt für den Kunden bei separater Beauftragung Dienstleistungen als externe Gefahrgutbeauftragte, Verpackerin und Beraterin (vgl. 1.3 hiervor).
      </>
    ),
  },
  {
    number: '6.2',
    title: 'Preisberechnung',
    listItems: [
      'Reisekosten (z. B. Anfahrtswege, Übernachtungen)',
      'Gebühren für behördliche Bewilligungen oder Genehmigungen',
      'Material- und Ausrüstungskosten;',
      'Dokumentations- und Zertifizierungsgebühren',
    ],
    renderContent: (listItems) => (
      <>
        Sofern Sephyre keine Offerte abgegeben hat bzw. die Parteien keine anderweitige Vereinbarung getroffen haben, verrechnet Sephyre die Dienstleistungserbringung im Gefahrengutbereich zu einem Stundensatz von CHF 190.--.
        <br />
        <br />
        Neben dem Stundenhonorar werden dem Kunden sämtliche zusätzlichen Aufwendungen in Rechnung gestellt, die im Rahmen der Leistungserbringung anfallen. Dazu gehören insbesondere, aber nicht abschliessend:
        {generateListItems(listItems || [])}
        Im Übrigen gelten die Bestimmungen in Ziff. 2.4 und 2.5.
      </>
    ),
  },
  {
    number: '6.3',
    title: 'Haftung',
    renderContent: () => (
      <>
        Die Haftung von Sephyre wird, soweit gesetzlich zulässig, vollumfänglich ausgeschlossen.
      </>
    ),
  },
  {
    number: '7',
    title: 'Besondere Bestimmungen für die Erbringung weiterer Dienstleistungen',
    renderContent: () => (<div />),
  },
  {
    number: '7.1',
    title: 'Leistungsumfang',
    renderContent: () => (
      <>
        Sephyre erbringt Verpackungsdienstleistungen und Lagerhaltungsdienstleistungen im Zusammenhang oder unabhängig von Transportaufträgen (vgl. 1.4 hiervor).
      </>
    ),
  },
  {
    number: '7.2',
    title: 'Preisberechnung',
    listItems: [
      'Reisekosten (z. B. Anfahrtswege, Übernachtungen)',
      'Gebühren für behördliche Bewilligungen oder Genehmigungen',
      'Material- und Ausrüstungskosten',
      'Dokumentations- und Zertifizierungsgebühren',
    ],
    renderContent: (listItems) => (
      <>
        Sofern Sephyre keine Offerte abgegeben hat bzw. die Parteien keine anderweitige Vereinbarung getroffen haben, verrechnet Sephyre die Dienstleistungserbringung zu einem Stundensatz von CHF 190.--.
        <br />
        <br />
        Neben dem Stundenhonorar werden dem Kunden sämtliche zusätzlichen Aufwendungen in Rechnung gestellt, die im Rahmen der Leistungserbringung anfallen. Dazu gehören insbesondere, aber nicht abschliessend:
        {generateListItems(listItems || [])}
        Im Übrigen gelten die Bestimmungen in Ziff. 2.4 und 2.5.
      </>
    ),
  },
  {
    number: '7.3',
    title: 'Haftung',
    renderContent: () => (
      <>
        Die Haftung von Sephyre wird, soweit gesetzlich zulässig, vollumfänglich ausgeschlossen.
      </>
    ),
  },
  {
    number: '8',
    title: 'Zahlungsbedingungen und Schlussbestimmungen',
    renderContent: () => (
      <>
        Die Bestimmungen unter Ziff. 8.1-8.11 gelten für sämtliche Verträge zwischen Sephyre und dem Kunden.
      </>
    ),
  },
  {
    number: '8.1',
    title: 'Zahlungsbedingungen',
    renderContent: () => (
      <>
        Forderungen werden 10 Tage nach Rechnungsstellung fällig. Verzugszinsen: 5% p.a. Rechnungen werden standardmässig per E-Mail zugestellt. Für Rechnungen in Papierform wird ein Zuschlag von 5% erhoben.
      </>
    ),
  },
  {
    number: '8.2',
    title: 'Kostenvorschüsse',
    renderContent: () => (
      <>
        Sephyre ist nicht verpflichtet, Frachten, Zölle, Gebühren sowie eigene Kosten vorzuschiessen und kann nach eigenem Ermessen vom Kunden Kostenvorschüsse verlangen, insbesondere wenn Zweifel an der Bonität des Kunden bestehen oder andere Gründe die Erhebung eines Kostenvorschusses erforderlich machen (z.B. bei hohen Frachten, Zölle oder Gebühren etc.).
      </>
    ),
  },
  {
    number: '8.3',
    title: 'Retentionsrecht',
    renderContent: () => (
      <>
        Sephyre hat ein Pfandrecht an den übergebenen Gütern. Nach unbenutztem Ablauf der Zahlungsfrist darf Sephyre die Güter ohne weitere Formalitäten freihändig verwerten.
      </>
    ),
  },
  {
    number: '8.4',
    title: 'Verrechnungsverbot',
    renderContent: () => (
      <>
        Die Verrechnung von Forderungen des Kunden mit Forderungen von Sephyre ist ausgeschlossen. Umgekehrt darf Sephyre offene Forderungen gegenüber dem Kunden mit Gegenforderungen des Kunden verrechnen.
      </>
    ),
  },
  {
    number: '8.5',
    title: 'Schadensersatzpflicht des Kunden',
    renderContent: () => (
      <>
        Der Kunde ist verpflichtet, Sephyre sämtliche Schäden zu ersetzen, die dieser infolge eines schuldhaften Verhaltens des Kunden entstehen. Dies umfasst sowohl direkte als auch indirekte Schäden, einschliesslich, aber nicht beschränkt auf entgangenen Gewinn, Betriebs- unterbrechung, Reputationsschäden sowie sonstige Folgeschäden. Die Ersatzpflicht gilt unabhängig davon, ob der Schaden durch eine Verletzung vertraglicher Pflichten, gesetzlicher Vorschriften oder sonstiger Obliegenheiten verursacht wurde.
      </>
    ),
  },
  {
    number: '8.6',
    title: 'Verjährung',
    renderContent: () => (
      <>
        Vorbehältlich abweichender zwingender gesetzlicher Bestimmungen verjähren sämtliche Ansprüche gegen Sephyre nach einem Jahr.
      </>
    ),
  },
  {
    number: '8.7',
    title: 'Übertragbarkeit von Rechten',
    renderContent: () => (
      <>
        Der Kunde darf seine Rechte aus diesem Vertrag nur mit Zustimmung von Sephyre ganz oder teilweise auf Dritte übertragen. Sephyre steht die Übertrag auf Dritte frei.
      </>
    ),
  },
  {
    number: '8.8',
    title: 'Salvatorische Klausel',
    renderContent: () => (
      <>
        Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, so wird hierdurch die Wirksamkeit der übrigen Bestimmungen nicht berührt. Die ungültige Bestimmung ist von den Parteien durch gültige Bestimmung zu ersetzen, die dem mit der ungültigen Bestimmung verfolgten wirtschaftlichen Zweck am nächsten kommen. Für den Fall einer Lücke gilt das Vorstehende entsprechend.
      </>
    ),
  },
  {
    number: '8.9',
    title: 'Höhere Gewalt',
    renderContent: () => (
      <>
        Sephyre ist von ihren vertraglichen Verpflichtungen befreit, sofem deren Erfüllung durch höhere Gewalt unmöglich wird oder erheblich erschwert ist. Höhere Gewalt umfasst unvorhersehbare, unvermeidbare und ausserhalb des Einflussbereichs von Sephyre liegende Ereignisse, wie Naturkatastrophen, Krieg, Streik, Epidemien oder Pandemien, behördliche Massnahmen oder erhebliche Betriebsstörungen. Sephyre haftet nicht für Verzögerungen oder Nichterfüllungen aufgrund solcher Ereignisse. Dauert die Behinderung länger als 30 Tage, sind beide Parteien berechtigt, den Vertrag aufzulösen. Bereits erbrachte Leistungen sind zu vergüten.
      </>
    ),
  },
  {
    number: '8.10',
    title: 'Änderungen der AGB',
    renderContent: () => (
      <>
        Sephyre behält sich das Recht vor, diese AGB jederzeit zu ändern oder zu ergänzen. Die jeweils aktuelle Version ist auf der Website von Sephyre oder in anderer geeigneter Weise verfügbar und gilt mit ihrer Veröffentlichung als verbindlich. Massgeblich ist die zum Zeitpunkt der jeweiligen Leistungserbringung gültige Fassung, sofern nicht ausdrücklich etwas anderes vereinbart wurde.
      </>
    ),
  },
  {
    number: '8.11',
    title: 'Anwendbares Recht und Gerichtsstand',
    renderContent: () => (
      <>
        Es gilt schweizerisches Recht. Ausschliesslicher Gerichtsstand ist der Sitz von Sephyre. Sephyre ist berechtigt, Forderungen auch am Wohnsitz des Kunden. geltend zu machen.
      </>
    ),
  },
];

export const sectionsEN: SectionItem[] = [
  {
    number: 'Introduction',
    title: '',
    renderContent: () => (
      <div>
        These General Terms and Conditions (GTC) govern the contractual relationship between Sephyre GmbH (hereinafter &quot;Sephyre&quot;) and its customers (hereinafter &quot;Customer/s&quot;) for the following services which Sephyre provides for its customers:
        {generateListItems([
          'Forwarding services: Sephyre acts as a freight forwarder that arranges transportation services and related services (see section1.1 ).',
          'Carrier services: In exhaustively defined cases, Sephyre acts as a carrier (see section1.2 ).',
          'Services in the dangerous goods sector: Sephyre provides various services in the dangerous goods sector (see section1.3 ).',
          'Other services: Sephyre provides additional services that are directly or indirectly related to transportation or are commissioned independently of it, such as packaging services and warehousing (see section ).1.4',
        ], 'bullet')}
        <p>
          Sephyre shall provide all services on the terms and
          conditions set out below. No other terms and conditions,
          whether communicated orally or in writing, shall have any
          effect unless expressly agreed to in writing by Sephyre on
          a case-by-case basis. Any additional or different terms and
          conditions contained in any document or e-mail transmitted
          by the Customer to Sephyre at any time, including, but not
          limited to, any general terms and conditions of the
          Customer, shall be deemed rejected by Sephyre and shall
          have no force or effect, even if such terms and conditions
          have not been expressly rejected by Sephyre.
        </p>
        <p>
          By placing an order with Sephyre, the customer accepts
          these General Terms and Conditions of Sephyre.
        </p>
        <p>
          These GTC, together with the order confirmation,
          constitute the entire agreement between Sephyre and the
          customer, unless the parties have agreed otherwise in
          writing. In the event of a conflict between Sephyre&apos;s order
          confirmation and the GTC, any deviating agreements in
          Sephyre&apos;s order confirmation shall take precedence.
        </p>
      </div>
    ),
  },
  {
    number: '1',
    title: 'Services',
    renderContent: () => (<div />),
  },
  {
    number: '1.1',
    title: 'Forwarding services',
    listItems: [
      'The conclusion of contracts with carriers, other freight forwarders, warehouse keepers and other third parties (e.g. customs clearance agents) for the execution of transportation and all associated services.',
      'The selection of suitable transport companies and warehouse keepers as well as the coordination of transport logistics.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre acts as an intermediary in the provision of forwarding services. For a fee, Sephyre organizes the shipment or forwarding of goods for the account of the customer, but in its own name.
        <br />
        <br />
        This includes in particular, but is not limited to:
        {generateListItems(listItems || [])}
        Sephyre&apos;s activities are limited to the arrangement and organization of transport and logistics services. This also applies if Sephyre agrees a fixed price with the customer for the transportation and other services and thus acts as a fixed-cost freight forwarder. In such a case, Sephyre also provides the agreed services in its own name, but remains the intermediary between the customer and the subcontractors commissioned by Sephyre (e.g. carriers, warehouse keepers). Sephyre itself does not carry out any transportation with its own means, unless this is agreed separately as a carrier service (cf. Clause 1.2).
      </>
    ),
  },
  {
    number: '1.2',
    title: 'Carrier services',
    listItems: [
      'Sephyre carries out transportation with its own means of transport (self-entry).',
      'Sephyre issues its own transport documents with an obligation to deliver.',
      'For purely European land transportation (subject to pure rail transport), unless Sephyre expressly designates itself as an intermediary and also acts as such.',
    ],
    renderContent: (listItems) => (
      <>
        In the cases defined below, Sephyre shall have the status of carrier.
        {generateListItems(listItems || [])}
      </>
    ),
  },
  {
    number: '1.3',
    title: 'Provision of services in the hazardous goods sector',
    listItems: [
      'External dangerous goods officers: Monitoring and advising the customer on compliance with legal regulations.',
      'Dangerous goods packaging: Proper packaging and labeling of dangerous goods in accordance with applicable regulations.',
      'Consulting: Support in the preparation of safety data sheets and training of employees.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre provides comprehensive services for the customer in the area of dangerous goods, including the following, if commissioned separately:
        {generateListItems(listItems || [])}
      </>
    ),
  },
  {
    number: '1.4',
    title: 'Further services',
    renderContent: () => (
      <>
        Sephyre shall provide further services for the customer, if commissioned separately, which are directly or indirectly related to transportation or are commissioned independently of it. In particular, but not exclusively, this includes packaging services and warehousing.
      </>
    ),
  },
  {
    number: '2',
    title: 'Offer, conclusion of contract and payment',
    renderContent: () => (
      <>
        The provisions under sections 2.1-2.5 shall apply to all contracts between Sephyre and the customer, unless deviated from under the special provisions pursuant to sections 4-7.
      </>
    ),
  },
  {
    number: '2.1',
    title: 'Non-binding offer (quotation)',
    renderContent: () => (
      <>
        All offers from Sephyre are non-binding.
      </>
    ),
  },
  {
    number: '2.2',
    title: 'Placing an order',
    renderContent: () => (
      <>
        The customer generally places the order with Sephyre online via the Sephyre homepage. Alternatively, the order can also be placed in writing (incl. electronic message by e-mail, SMS etc.) or verbally (by telephone). After receipt of the order by Sephyre, the customer receives a confirmation of receipt, which merely documents receipt of the order. This confirmation of receipt does not constitute an order confirmation within the meaning of 2.3 and does not constitute a claim to acceptance or execution of the order by Sephyre.
        <br />
        <br />
        If an order is placed verbally or by telephone, the customer shall bear the risk of transmission errors until the order confirmation in accordance with section 2.3 is received by Sephyre. Sephyre accepts no liability for damages or delays resulting from incorrect or incomplete transmission.
      </>
    ),
  },
  {
    number: '2.3',
    title: 'Formation of the contract',
    listItems: [
      'upon dispatch or submission of the order confirmation by Sephyre or',
      'with the handover of the goods to the carrier, if this takes place before the order confirmation.',
    ],
    renderContent: (listItems) => (
      <>
        The contract is only concluded:
        {generateListItems(listItems || [], 'alphabetical')}
        Sephyre reserves the right to refuse orders at any time and without giving reasons. There is no entitlement to acceptance or execution of an order.
        <br />
        <br />
        Orders that have already been accepted can be canceled at any time for important reasons or for reasons for which Sephyre is not responsible. Such reasons include, but are not limited to, force majeure within the meaning of section 8.9, strikes or work stoppages, unforeseeable technical defects in means of transportation, insolvency or cessation of operations of a contracted carrier or subcontractor, cancellation or non-performance of the order by the carrier or subcontractor, safety risks or official requirements that prevent or excessively complicate the execution of the transport.
        <br />
        <br />
        In such a case, Sephyre shall inform the customer immediately. Further claims of the customer, in particular claims for damages, are excluded.
      </>
    ),
  },
  {
    number: '2.4',
    title: 'Remuneration from Sephyre',
    renderContent: () => (
      <>
        The customer shall owe Sephyre AG the remuneration in accordance with Sephyre&apos;s offer, unless the parties have expressly agreed otherwise. If Sephyre has not submitted an offer or the parties have not reached any other agreement, the remuneration owed by the Customer shall be calculated in accordance with the Special Provisions for the respective services (cf. Sections 4.2, 5.2, 6.2 and 7.2)
        <br />
        <br />
        Unless otherwise agreed in writing, all prices are net (excluding VAT). VAT is payable by the customer in addition.
        <br />
        <br />
        Prices are subject to change due to fuel surcharges, exchange rate fluctuations or official levies. Sephyre is entitled to pass on such price increases to the customer.
        <br />
        <br />
        If changes occur after the order has been placed which require an adjustment of the price (e.g. additional services, delays, etc.), Sephyre reserves the right to adjust the price calculation accordingly. Sephyre is entitled to pass on price increases to the customer.
      </>
    ),
  },
  {
    number: '2.5',
    title: 'Reimbursement of advances and expenses',
    renderContent: () => (
      <>
        The customer shall reimburse Sephyre for all advances, expenses and utilization made in the interest of the customer, plus interest of 5% from the due date. Sephyre shall not be liable for the incorrect levying of freight, customs duties, charges or fees, and the customer shall indemnify Sephyre against all claims in this respect.
      </>
    ),
  },
  {
    number: '3',
    title: 'Order processing',
    renderContent: () => (
      <>
        The provisions under sections 3.1-3.11 shall apply to all contracts between Sephyre and the customer, unless deviated from under the special provisions pursuant to sections 4-7.
      </>
    ),
  },
  {
    number: '3.1',
    title: 'Checking the order',
    renderContent: () => (
      <>
        Sephyre checks the order for plausibility, but is not obliged to check the consignment, transport containers, weights, dimensions or other details (e.g. dangerous goods classes), unless this has been expressly agreed. Sephyre shall inform the customer immediately in the event of any ambiguities.
      </>
    ),
  },
  {
    number: '3.2',
    title: 'Obligation of the customer to provide information',
    listItems: [
      'Dangerous goods (including UN number, classification, packing group and required safety measures);',
      'Customs clearance (including customs tariff number); and',
      'any necessary export and import licenses (e.g. for dual-use goods, sanctioned goods, technologies requiring approval or other goods that are subject to export, import or transit restrictions).',
    ],
    renderContent: (listItems) => (
      <>
        When placing the order, the customer is obliged to provide Sephyre with all information required for the proper processing of the order in full and correctly and to hand over the necessary documents. This applies in particular, but not exclusively, to:
        {generateListItems(listItems || [])}
        Without express information and submission of corresponding documents by the customer, Sephyre may assume that no special requirements and no authorization obligations exist. The customer is liable for incorrect, incomplete or omitted information and indemnifies Sephyre against all resulting damages, penalties or other detrimental consequences. This also applies in particular to claims by carriers, subcontractors, other third parties involved in the transportation chain as well as to official orders, requirements, fines or other claims by authorities which are asserted against Sephyre due to incorrect or incomplete information provided by the customer.
      </>
    ),
  },
  {
    number: '3.3',
    title: "Modification of the customer's order",
    renderContent: () => (
      <>
        Sephyre AG is entitled to make changes to the customer&apos;s order insofar as this is necessary for the proper execution of the order. This includes in particular the correction or completion of incorrect or incomplete information in the order form, such as incorrect or incomplete customs tariff numbers, inaccurate recipient or sender addresses, incorrect weight or volume information and incorrect classifications of dangerous goods. Necessary information can also be added in order to comply with legal or official regulations.
        <br />
        <br />
        If necessary, Sephyre can also adapt the order modalities. This includes changing the selected means of transportation, for example switching from road to rail. transport or from air to sea freight. The transport route can also be modified if necessary. In addition, Sephyre can adjust the storage or handling conditions, particularly in the case of special requirements such as temperature control or safety precautions. Should an adjustment of the delivery times be necessary due to unavoidable delays, Sephyre is entitled to modify these accordingly.
        <br />
        <br />
        All changes made by Sephyre are visible to the customer in the Sephyre booking system or in the e-mail notification from Sephyre. The customer is obliged to check any changes made by Sephyre without delay. Complaints must be reported to Sephyre immediately in writing, otherwise the changes shall be deemed to have been accepted. Any additional costs incurred as a result of the changes shall be borne in full by the customer. Sephyre shall not be liable for delays or additional costs caused by incomplete or incorrect information provided by the customer.
      </>
    ),
  },
  {
    number: '3.4',
    title: 'Obligation of the customer to check transmitted documents',
    renderContent: () => (
      <>
        The customer is obliged to check the documents sent to him by Sephyre, in particular the order confirmation, transport documents, air waybills (AWB), consignment notes, dangerous goods accompanying documents, customs documents, invoices and proof of delivery, for correctness and completeness immediately upon receipt.
        <br />
        <br />
        Complaints must be reported to Sephyre immediately in writing. If no complaint is made in good time, the documents shall be deemed to have been accepted by the customer.
      </>
    ),
  },
  {
    number: '3.5',
    title: 'Responsibility for loading aids and packaging',
    renderContent: () => (
      <>
        The customer is responsible for ensuring that the loading equipment is suitable for transportation. Proper packaging of the goods is the responsibility of the customer, unless Sephyre is expressly commissioned with the packaging.
      </>
    ),
  },
  {
    number: '3.6',
    title: 'Delivery times',
    renderContent: () => (
      <>
        Delivery deadlines are only binding if they are agreed in writing. Otherwise, Sephyre shall not be liable for compliance with delivery dates.
      </>
    ),
  },
  {
    number: '3.7',
    title: 'High-value goods or goods requiring special treatment',
    renderContent: () => (
      <>
        The customer must expressly specify high-value goods or goods that require special handling (e.g. refrigeration, dangerous goods regulations) in the order and communicate the necessary measures.
      </>
    ),
  },
  {
    number: '3.8',
    title: 'Transport insurance',
    renderContent: () => (
      <>
        Sephyre shall only take out transport insurance at the express request of the customer.
        <br />
        <br />
        If the customer requests insurance without giving specific instructions, the most comprehensive transportation insurance possible will be taken out, whereby indirect or consequential damages (e.g. loss of profit) are not covered. The customer is responsible for supplementary insurance.
      </>
    ),
  },
  {
    number: '3.9',
    title: 'Loading and unloading',
    renderContent: () => (
      <>
        The sender or recipient is responsible for loading and unloading the goods. If the driver assists with these activities, he is deemed to be an auxiliary of the consignor or consignee.
      </>
    ),
  },
  {
    number: '3.10',
    title: 'Interim storage',
    renderContent: () => (
      <>
        If the recipient refuses to accept the goods or if the consignment is held up en route for reasons for which Sephyre is not responsible, Sephyre shall be entitled to store the goods at the expense and risk of the customer.
      </>
    ),
  },
  {
    number: '3.11',
    title: 'Cash on delivery (COD)',
    renderContent: () => (
      <>
        Cash on delivery (COD) is only carried out on written instruction from the customer and for an additional fee (COD commission). Delivery is only made against cash payment or against an irrevocable promise of payment from a first-class bank.
      </>
    ),
  },
  {
    number: '4',
    title: 'Special provisions for forwarding services',
    renderContent: () => (<div />),
  },
  {
    number: '4.1',
    title: 'Scope of services',
    renderContent: () => (
      <>
        Sephyre acts as a forwarding agent within the meaning of section 1.1 above and arranges transportation services. Sephyre organizes the dispatch or forwarding of goods and concludes contracts with third parties (e.g. carriers, warehouse keepers) in its own name but for the account of the customer.
      </>
    ),
  },
  {
    number: '4.2',
    title: 'Price calculation',
    listItems: [
      <>
        <b>Costs and fees of the contractors commissioned</b>
        <br />
        This includes in particular the fees for carriers, sub-carriers, customs clearance agencies, warehouse keepers and other service providers involved in the transportation chain.
      </>,
      <>
        <b>Commission fee from Sephyre</b>
        <br />
        In addition, Sephyre charges a commission fee for the coordination and handling of the forwarding service. This is usually 10% of the total order value, unless otherwise agreed.
        <br />
        <br />
        Sephyre reserves the right to charge a different commission fee in special cases, in particular in the case of exceptional expenditure or special customer requirements.
      </>,
      <>
        <b>Additional costs</b>
        <br />
        If additional costs are incurred in the course of transportation, these will be invoiced separately to the customer. The customer undertakes to bear all additional costs insofar as they are necessary for the proper execution of the order. In all other respects, the provisions in sections 2.4 and 2.5 shall apply.
      </>,
    ],
    renderContent: (listItems) => (
      <>
        If Sephyre has not submitted an offer or the parties have not reached any other agreement, the price for the forwarding services shall be made up of the following components:
        {generateListItems(listItems || [], 'alphabetical')}
      </>
    ),
  },
  {
    number: '4.3',
    title: 'Liability',
    listItems: [
      'Loss of or damage to the goods: 1.15 special drawing rights per kilogram gross weight of the affected consignment.',
      'Damage caused by delay: half of the net freight amount.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre shall be liable for the careful execution of the order. Liability for subcontractors is limited to their careful selection and instruction.
        <br />
        <br />
        Subject to any mandatory statutory provisions to the contrary, the following maximum liability limits apply to Sephyre&apos;s liability:
        {generateListItems(listItems || [])}
        Sephyre&apos;s liability per loss event is limited to a maximum of 10,000 special drawing rights (SDR). Any further liability is excluded to the extent permitted by law. Sephyre shall not be liable for damages or delays caused by force majeure.
      </>
    ),
  },
  {
    number: '5',
    title: 'Special provisions for carrier services',
    renderContent: () => (<div />),
  },
  {
    number: '5.1',
    title: 'Scope of services',
    renderContent: () => (
      <>
        In the cases listed exhaustively in accordance with section 1.2, Sephyre acts as carrier and carries out transportation in accordance with the conditions agreed with the customer.
      </>
    ),
  },
  {
    number: '5.2',
    title: 'Price calculation',
    listItems: [
      <>
        <b>Basis of the price calculation</b>
        <br />
        The price is calculated on the basis of the services actually provided and the distance traveled, the weight, volume, the selected means of transport or other transport-relevant factors. Surcharges for special services such as express delivery, temperature-controlled transportation or special transportation will be charged separately.
      </>,
      <>
        <b>Additional costs</b>
        <br />
        All additional costs incurred in connection with the transportation (e.g. tolls, customs clearance costs, waiting times, demurrage, handling costs) shall be invoiced separately to the customer. The customer undertakes to bear all additional costs. In all other respects, the provisions in sections 2.4 and 2.5 shall apply.
      </>,
    ],
    renderContent: (listItems) => (
      <>
        Unless Sephyre has submitted an offer or the parties have agreed otherwise, the price for Sephyre&apos;s carrier services shall be determined in accordance with the following principles:
        {generateListItems(listItems || [], 'alphabetical')}
      </>
    ),
  },
  {
    number: '5.3',
    title: 'Liability',
    listItems: [
      'Loss of or damage to goods in the case of purely Swiss domestic road transportation: 1.15 special drawing rights per kilogram of gross weight of the affected consignment.',
      'Loss of or damage to goods in cross-border European road transportation: 8.33 special drawing rights per kilogram of gross weight of the affected consignment.',
      'Damage caused by delay: half of the net freight amount.',
    ],
    renderContent: (listItems) => (
      <>
        Sephyre shall bear the carrier&apos;s liability for the entire transport route. Sephyre reserves the right to self-assume liability for only part of the journey.
        <br />
        <br />
        Subject to any mandatory statutory provisions to the contrary, the following maximum liability limits apply to Sephyre&apos;s liability:
        {generateListItems(listItems || [])}
        Sephyre&apos;s liability per loss event is limited to a maximum of 10,000 special drawing rights (SDR). Any further liability is excluded to the extent permitted by law. Sephyre shall not be liable for damages or delays caused by force majeure.
      </>
    ),
  },
  {
    number: '6',
    title: 'Special provision for the provision of services in the dangerous goods sector',
    renderContent: () => (<div />),
  },
  {
    number: '6.1',
    title: 'Scope of services',
    renderContent: () => (
      <>
        Sephyre provides services for the customer as an external dangerous goods officer, packer and consultant if commissioned separately (see 1.3 above).
      </>
    ),
  },
  {
    number: '6.2',
    title: 'Price calculation',
    renderContent: () => (
      <>
        If Sephyre has not submitted an offer or the parties have not reached any other agreement, Sephyre shall charge an hourly rate of CHF 190 for the provision of services in the dangerous goods sector.
        <br />
        <br />
        In addition to the hourly fee, the client will be invoiced for all additional expenses incurred in the course of providing the service. These include in particular, but are not limited to:
        {generateListItems([
          'Travel expenses (e.g. travel, overnight stays)',
          'Fees for official authorizations or permits',
          'Material and equipment costs;',
          'Documentation and certification fees',
        ])}
        In all other respects, the provisions in sections 2.4 and 2.5 apply.
      </>
    ),
  },
  {
    number: '6.3',
    title: 'Liability',
    renderContent: () => (
      <>
        Sephyre&apos;s liability is fully excluded to the extent permitted by law.
      </>
    ),
  },
  {
    number: '7',
    title: 'Special provisions for the provision of further services',
    renderContent: () => (<div />),
  },
  {
    number: '7.1',
    title: 'Scope of services',
    renderContent: () => (
      <>
        Sephyre provides packaging services and warehousing services in connection with or independently of transportation orders (see 1.4 above).
      </>
    ),
  },
  {
    number: '7.2',
    title: 'Price calculation',
    renderContent: () => (
      <>
        If Sephyre has not submitted an offer or the parties have not reached any other agreement, Sephyre shall charge an hourly rate of CHF 190 for the provision of services.
        <br />
        <br />
        In addition to the hourly fee, the client shall be invoiced for all additional expenses incurred in the course of providing the service. These include in particular, but are not limited to:
        {generateListItems([
          'Travel expenses (e.g. travel, overnight stays)',
          'Fees for official authorizations or permits',
          'Material and equipment costs',
          'Documentation and certification fees',
        ])}
        In all other respects, the provisions in sections 2.4 and 2.5 apply.
      </>
    ),
  },
  {
    number: '7.3',
    title: 'Liability',
    renderContent: () => (
      <>
        Sephyre&apos;s liability is fully excluded to the extent permitted by law.
      </>
    ),
  },
  {
    number: '8',
    title: 'Terms of payment and final provisions',
    renderContent: () => (
      <>
        The provisions under sections 8.1-8.11 apply to all contracts between Sephyre and the customer.
      </>
    ),
  },
  {
    number: '8.1',
    title: 'Terms of payment',
    renderContent: () => (
      <>
        Receivables are due 10 days after invoicing. Interest on arrears: 5% p.a. Invoices are sent by e-mail as standard. A surcharge of 5% is levied for invoices in paper form.
      </>
    ),
  },
  {
    number: '8.2',
    title: 'Cost advances',
    renderContent: () => (
      <>
        Sephyre is not obliged to advance freight, customs duties, fees or its own costs and may, at its own discretion, require the customer to advance costs, in particular if there are doubts about the customer&apos;s creditworthiness or if other reasons make it necessary to advance costs (e.g. in the case of high freight, customs duties or fees, etc.).
      </>
    ),
  },
  {
    number: '8.3',
    title: 'Right of retention',
    renderContent: () => (
      <>
        Sephyre has a lien on the goods handed over. After unused expiry of the payment period, Sephyre may freely dispose of the goods without further formalities.
      </>
    ),
  },
  {
    number: '8.4',
    title: 'Prohibition of offsetting',
    renderContent: () => (
      <>
        The offsetting of claims of the customer against claims of Sephyre is excluded. Conversely, Sephyre may offset outstanding claims against the customer with counterclaims of the customer.
      </>
    ),
  },
  {
    number: '8.5',
    title: 'Liability of the customer for damages',
    renderContent: () => (
      <>
        The customer is obliged to compensate Sephyre for all damages incurred by Sephyre as a result of culpable conduct on the part of the customer. This includes both direct and indirect damages, including, but not limited to, loss of profit, business interruption, reputational damage and other consequential damages. The obligation to pay compensation applies regardless of whether the damage was caused by a breach of contractual obligations, statutory provisions or other obligations.
      </>
    ),
  },
  {
    number: '8.6',
    title: 'Statute of limitations',
    renderContent: () => (
      <>
        Subject to any mandatory statutory provisions to the contrary, all claims against Sephyre shall lapse after one year.
      </>
    ),
  },
  {
    number: '8.7',
    title: 'Transferability of rights',
    renderContent: () => (
      <>
        The customer may only transfer his rights under this contract in whole or in part to third parties with the consent of Sephyre. Sephyre is free to transfer the rights to third parties.
      </>
    ),
  },
  {
    number: '8.8',
    title: 'Severability clause',
    renderContent: () => (
      <>
        Should any provision of these GTC be or become invalid, this shall not affect the validity of the remaining provisions. The invalid provision shall be replaced by the parties with a valid provision that comes closest to the economic purpose of the invalid provision. In the event of a loophole, the above shall apply accordingly.
      </>
    ),
  },
  {
    number: '8.9',
    title: 'Force majeure',
    renderContent: () => (
      <>
        Sephyre shall be released from its contractual obligations if their fulfillment is made impossible or considerably more difficult by force majeure. Force majeure includes unforeseeable, unavoidable events that are beyond Sephyre&apos;s control, such as natural disasters, war, strikes, epidemics or pandemics, official measures or significant operational disruptions. Sephyre shall not be liable for delays or non-fulfillment due to such events. If the hindrance lasts longer than 30 days, both parties are entitled to terminate the contract. Services already rendered shall be remunerated.
      </>
    ),
  },
  {
    number: '8.10',
    title: 'Changes to the GTC',
    renderContent: () => (
      <>
        Sephyre reserves the right to amend or supplement these GTC at any time. The current version shall be available on the Sephyre website or in another suitable manner at and shall be deerned binding upon publication. The version valid at the time of the respective service provision shall be authoritative, unless expressly agreed otherwise.
      </>
    ),
  },
  {
    number: '8.11',
    title: 'Applicable law and place of jurisdiction',
    renderContent: () => (
      <>
        Swiss law shall apply. The exclusive place of jurisdiction is the registered office of Sephyre. Sephyre is also entitled to assert claims at the customer&apos;s place of residence.
      </>
    ),
  },
];

export default {
  sectionsDE,
  sectionsEN,
};
/* eslint-enable max-len */
